import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../contexts/AppContext';
import { isNil, isString, round, groupBy, orderBy, sortBy } from 'lodash';
import ErrorBoundary from '../../../common/error-boundary';
import { DIMENSION } from '../../../../enum/common.enum';
import dayjs from 'dayjs';
import { getSnapshotDateStr } from '../../../../utils/DateUtil';
import SingleSegmentChart from '../../../base/single-segment-chart';
import { BarChartOutlined, LineChartOutlined } from '@ant-design/icons';
import { kpiValue, toCamelCase } from '../../../../utils/farecast-util';
import { useTralitSetting, NAME_PATH } from '../../../../hooks/useTralitSetting';
import { useLabel } from '../../../../hooks/useLabel';

const CustomizeForecastChart: React.FC<any> = (props) => {
  const { authInfo, echartTheme, globalEvent, setGlobalEvent, color } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const { getCompareLabel } = useLabel();
  const { property } = props;
  const [chartLoading, setChartLoading] = useState<any>(false);
  const [options, setOptions] = useState<any>();
  const titleObj = useMemo(() => ({ title: property.field.label, subtitle: '' }), [property]);
  let labelShowFlag = false;

  const chartData = useMemo(() => JSON.parse(property.dataSource.mockData), [property]);

  const dimensionMap = useMemo(
    () => ({
      [DIMENSION.DAY]: '日',
      [DIMENSION.WEEK]: '週',
      [DIMENSION.MONTH]: '月',
    }),
    []
  );

  const getColor = (dataValue, planValue) => {
    const value = round(planValue ? dataValue / planValue : 0, 3);
    if (value >= 1) {
      return color.successColor;
    }
    if (value >= 0.8) {
      return color.warningColor;
    }
    return color.errorColor;
  };

  const initContentData = (fieldKey) => {
    const { computed } = chartData;
    const { current: dataTotal, target: planTotal } = computed;
    const dataValue = dataTotal ? dataTotal[fieldKey] : null;
    const planValue = planTotal ? planTotal[fieldKey] : null;
    const radio = isNil(planValue) ? (
      '-'
    ) : (
      <span style={{ color: getColor(dataValue, planValue) }}>
        {round((planValue ? dataValue / planValue : 0) * 100, 1)}
        <span className="percent-label">%</span>
      </span>
    );
    const diff = isNil(planValue) ? (
      '-'
    ) : (
      <span style={{ color: getColor(dataValue, planValue) }}>{kpiValue(dataValue - planValue, property.field.unitValue, true)}</span>
    );
    const contentData = {
      items: [
        {
          title: (
            <>
              <span>値{property.field.unitName ? `(${property.field.unitName})` : ''}</span>
              {/* <TooltipWithLineBreak
                content={<InfoCircleOutlined style={{ marginLeft: 2, marginRight: 2, fontSize: 14, color: color.secondaryColor }} />}
                tooltips={t('tooltips.yMinZeroTips')}
              /> */}
            </>
          ),
          value: kpiValue(dataValue, property.field.unitValue, false)?.toLocaleString() || '-',
        },
        {
          title: getCompareLabel(property.filter),
          value: property.filter.switchValue ? radio : diff,
        },
      ],
      styles: {
        fontSize: fontSize.chart,
      },
    };
    return contentData;
  };

  const getOption = (field) => {
    const { current, target } = chartData;
    const { fieldKey: fieldKey_, label, compute } = field;
    const fieldKey = toCamelCase(fieldKey_);
    const xAxisData = Object.keys(groupBy([...current, ...target], property.filter.dimension)).sort();

    const aggregate = (data: any[], total = false) => {
      if (total) {
        const sorted = sortBy(data, property.filter.dimension);
        let totalResult = 0;
        return sorted.map((item) => {
          totalResult += item[fieldKey];
          return {
            ...item,
            [fieldKey]: totalResult,
          };
        });
      }
      return sortBy(data, property.filter.dimension);
    };

    const generatePlanRatioData = (data: any[], goal: any[]) => {
      const resultArray = [];
      data.forEach((dataItem) => {
        const goalData = goal.find((item) => item[property.filter.dimension] === dataItem[property.filter.dimension]);
        if (goalData && goalData[fieldKey]) {
          const newData = {
            [property.filter.dimension]: dataItem[property.filter.dimension],
            actualPlanRatio: dataItem[fieldKey] / goalData[fieldKey],
          };
          resultArray.push(newData);
        }
      });

      return resultArray;
    };

    const dataSource = aggregate(current, true);
    const planSource = aggregate(target, true);
    const planRatioSource = generatePlanRatioData(dataSource, planSource);

    const dimensionFormat = {
      [DIMENSION.DAY]: 'YYYY-MM-DD',
      [DIMENSION.WEEK]: 'YYYY-MM-DD',
      [DIMENSION.MONTH]: 'YYYY-MM',
    };

    const toolTipFormatter = (params) => {
      const [series] = params;
      const dimensionValue = dayjs(series.data[property.filter.dimension]).format(dimensionFormat[property.filter.dimension]);
      const labelStr = params
        .map((param) => {
          return `${param ? `<label>${param.marker || ''} ${param.seriesName}: </label>` : ''}<br/>`;
        })
        .join('');
      const valueStr = params
        .map((param) => {
          const kpi = param?.dimensionNames[param.encode.y[0]];
          const value = param?.value[kpi];
          if (param?.dimensionNames[1] === 'actualPlanRatio') {
            return `${param ? `<label>${kpiValue(value, 0.01)}%</label>` : ''}<br/>`;
          }
          return `${param ? `<label>${kpiValue(value, property.field.unitValue)}</label>` : ''}<br/>`;
        })
        .join('');
      return `<div class="tooltip-opacity" style="display: flex;flex-direction: column; ">
        <b>${dimensionValue}${dimensionMap[property.filter.dimension]}</b>
        <div style="display: flex;gap: 10px">
          <div>
            ${labelStr}
          </div>
          <div class="text-right">
            ${valueStr}
          </div>
        </div>
      </div>`;
    };

    return {
      title: {
        value: label,
        styles: {
          margin: 0,
        },
      },
      tooltips: ['zoomIn', 'reset'],
      contentList: initContentData(fieldKey),
      segment: {
        list: [
          {
            key: 'CarPLStatistics',
            icon: <BarChartOutlined />,
            value: 'statistics',
          },
          {
            key: 'CarPLTotal',
            icon: <LineChartOutlined />,
            value: 'total',
          },
        ],
        default: 'statistics',
      },
      chartOptions: {
        statistics: {
          animation: false,
          xAxis: {
            type: 'category',
            name: dimensionMap[property.filter.dimension],
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => dayjs(value).format(dimensionFormat[property.filter.dimension]),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            data: xAxisData,
          },
          yAxis: {
            type: 'value',
            name: '値',
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => kpiValue(value, property.field.unitValue),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            min: 0,
          },
          grid: {
            top: 45,
            right: 60,
            bottom: 40,
            left: 55,
          },
          legend: {
            // data: [t('kpi.new'), t('kpi.existing'), leadingIndicator],
            // selected: selectedLegend.statistics,
          },
          tooltip: {
            trigger: 'axis',
            appendToBody: true,
            axisPointer: {
              lineStyle: {
                width: 2,
              },
            },
            formatter: toolTipFormatter,
          },
          series: [
            {
              name: label,
              type: 'bar',
              encode: {
                x: property.filter.dimension,
                y: fieldKey,
              },
              datasetIndex: 0,
              markLine: {
                symbol: ['none', 'none'],
                lineStyle: {
                  color: color.errorColor,
                  width: 2,
                },
                label: {
                  fontSize: fontSize.chart,
                  formatter: (params) => {
                    const { name, data } = params;
                    return `${name}: ${data.week}`;
                  },
                  color: color.errorColor,
                  // fontSize: getSizeByMap(sizeMode, sizeMapBase, 'series'),
                },
                data: [
                  {
                    name: `今${getDimensionLabel(property.filter.dimension)}`,
                    xAxis: getXAxisIndex(getSnapshotDateStr(dayjs(property.snapshot), property.filter.dimension)),
                    week: getSnapshotDateStr(dayjs(property.snapshot), property.filter.dimension),
                  },
                ],
              },
            },
            {
              name: getCompareLabel(property.filter, false),
              type: 'line',
              symbol: 'none',
              datasetIndex: 1,
              step: 'middle',
              encode: {
                x: property.filter.dimension,
                y: fieldKey,
              },
              itemStyle: {
                color: color.warningColor,
              },
              lineStyle: {
                color: color.warningColor,
              },
              z: 10,
            },
          ],
          dataset: [
            {
              source: aggregate(current),
            },
            {
              source: aggregate(target),
            },
          ],
        },
        total: {
          animation: false,
          xAxis: {
            type: 'category',
            name: dimensionMap[property.filter.dimension],
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            data: xAxisData,
          },
          yAxis: [
            {
              type: 'value',
              name: '値',
              nameGap: 10,
              axisLabel: {
                fontSize: fontSize.chart,
                formatter: (value) => kpiValue(value, property.field.unitValue),
              },
              nameTextStyle: {
                fontSize: fontSize.chart,
              },
              min: 0,
            },
            {
              type: 'value',
              name: label,
              nameGap: 10,
              offset: 10,
              axisLabel: {
                fontSize: fontSize.chart,
                formatter: (value) => `${kpiValue(value, 0.01)}%`,
              },
              nameTextStyle: {
                fontSize: fontSize.chart,
              },
              min: 0,
            },
          ],
          grid: {
            top: 45,
            right: 60,
            bottom: 40,
            left: 55,
          },
          legend: {
            // selected: selectedLegend.total,
          },
          tooltip: {
            trigger: 'axis',
            appendToBody: true,
            axisPointer: {
              lineStyle: {
                width: 2,
              },
            },
            formatter: toolTipFormatter,
          },
          series: [
            {
              name: label,
              type: 'line',
              symbol: 'none',
              datasetIndex: 0,
              encode: {
                x: property.filter.dimension,
                y: fieldKey,
              },
              markLine: {
                symbol: ['none', 'none'],
                lineStyle: {
                  color: color.errorColor,
                  width: 2,
                },
                label: {
                  formatter: (params) => {
                    const { name, data } = params;
                    return `${name}: ${data.week}`;
                  },
                  color: color.errorColor,
                  fontSize: fontSize.chart,
                },
                data: [
                  {
                    name: `今${getDimensionLabel(property.filter.dimension)}`,
                    xAxis: getXAxisIndex(getSnapshotDateStr(dayjs(property.snapshot), property.filter.dimension)),
                    week: getSnapshotDateStr(dayjs(property.snapshot), property.filter.dimension),
                  },
                ],
              },
            },
            {
              name: getCompareLabel(property.filter, false),
              type: 'line',
              symbol: 'none',
              datasetIndex: 1,
              yAxisIndex: 0,
              itemStyle: {
                color: color.warningColor,
              },
              lineStyle: {
                color: color.warningColor,
              },
              encode: {
                x: property.filter.dimension,
                y: fieldKey,
              },
              z: 10,
            },
            {
              name: `実績${getCompareLabel(property.filter, false)}比`,
              type: 'line',
              symbol: 'none',
              datasetIndex: 2,
              yAxisIndex: 1,
              encode: {
                x: property.filter.dimension,
                y: 'actualPlanRatio',
              },
            },
          ],

          dataset: [
            {
              source: dataSource,
            },
            {
              source: planSource,
            },
            {
              source: planRatioSource,
            },
          ],
        },
      },
      height: 'calc(100% - 80px)',
    };
  };

  const getXAxisIndex = (data) => {
    if (chartData) {
      return Object.keys(groupBy([...chartData.current, ...chartData.target], property.filter.dimension))
        .sort()
        .findIndex((item) => {
          return `${item}` === `${data}`;
        });
    }
    return -1;
  };

  if (property?.chartOptions?.options) {
    const op = isString(property.chartOptions.options) ? JSON.parse(property.chartOptions.options) : property.chartOptions.options;
    labelShowFlag = op.series[0]?.label?.show ?? false;
  }

  useEffect(() => {
    setOptions(getOption(property.field));
  }, [property, fontSize]);

  const getDimensionLabel = (dimension: DIMENSION) => {
    const dimensionMap = {
      [DIMENSION.DAY]: '日',
      [DIMENSION.WEEK]: '週',
      [DIMENSION.MONTH]: '月',
    };
    return dimensionMap[dimension];
  };

  return (
    <ErrorBoundary>
      {options && (
        <SingleSegmentChart
          {...options}
          title={{
            value: (
              <>
                <span style={{ fontSize: fontSize.chartTitle }}>{titleObj.title}</span>{' '}
                <span style={{ float: 'right' }}>{titleObj.subtitle}</span>
              </>
            ),
            styles: {},
          }}
          showSeriesLabel={labelShowFlag}
          loading={chartLoading}
          tooltips={['zoomIn']}
          isBank={!chartData || chartData.length === 0}
        />
      )}
    </ErrorBoundary>
  );
};

export default CustomizeForecastChart;
